import React, { useRef, useCallback } from 'react';
import styled from 'styled-components';

import ActionAdapterEditor from './ActionAdapterEditor';
import ActionAdapterRunsLog from './ActionAdapterRunsLog';
import ActionAdapterNewEditor from './ActionAdapterNewEditor';

import FullPageModalRoute from 'src/presentation/components/pages/glados/components/FullPageModalIRoute';
import { Iframe } from 'src/presentation/components/pages/glados/components/Iframe';
import { Spinner } from 'src/presentation/components/pages/shared/Spinner';
import useIframeSrc from 'src/presentation/components/pages/glados/actionAdapters/hooks/useIframeSrc';
import {
  postMessageToIframe,
  useListenMessage,
} from 'src/presentation/components/pages/glados/actionAdapters/hooks/useListenMessage';
import useRouter from 'src/presentation/hooks/useRouter';
import { useDispatch } from 'react-redux';
import { toastError, toastSuccess } from 'src/actions/toast';
import ActivateInactivateModal, {
  ModalWrapperRef,
} from 'src/presentation/components/pages/glados/components/ActivateInactivateModal';
import GuidesModal, {
  ModalWrapperRef as GuidesModalRef,
} from 'src/presentation/components/pages/glados/components/GuidesModal';
import DuplicateConfirmationModal, {
  DuplicateModalWrapperRef,
} from 'src/presentation/components/pages/glados/components/DuplicateConfirmationModal';

export interface GuideInfo {
  id: string;
  name: string;
  cyberThreads: { id: string; name: string }[];
}
export type ActionAdapter = { id: string; active: boolean; name: string };

const ActionAdapters = ({ root }) => {
  const { srcUrl, loading, handleIframeLoad, handleIframeError } = useIframeSrc();
  const modalRef = useRef<ModalWrapperRef>(null);
  const guidesModalRef = useRef<GuidesModalRef>(null);
  const duplicateModalRef = useRef<DuplicateModalWrapperRef>(null);

  const dispatch = useDispatch();
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const { history } = useRouter();

  const push = useCallback((path: string) => history.push(`${root}${path}`), [history, root]);
  const replace = useCallback((path: string) => history.replace(`${root}${path}`), [history, root]);

  useListenMessage(
    [
      'addActionAdapter',
      'edit',
      'resize',
      'returnToAppAction',
      'viewLogs',
      'editReplace',
      'importFinished',
      'showActiveInactiveDialog',
      'showGuidesDialog',
      'showDuplicatedDialog',
      'updateActionAdapter',
      'duplicateActionAdapter',
      'actionAdapterUpdated',
      'actionAdapterDuplicated',
      'actionAdapterFailedToUpdate',
      'openActionAdapterRuns',
      'returnToAppAction',
    ],
    e => {
      const { type, id } = e.data;
      if (type === 'edit' && id) push(`/${id}`);
      if (type === 'addActionAdapter') push('/new');
      if (type === 'viewLogs' && id) push(`/${id}/runs`);
      if (type === 'editReplace' && id) replace(`/${id}`);
      if (type === 'importFinished') {
        dispatch(toastSuccess('Import completed.'));
      }
      if (type === 'showActiveInactiveDialog') {
        handleAddActionAdapter(e.data.actionAdapter, e.data.guides);
      }
      if (type === 'showGuidesDialog') {
        handleShowGuidesDialog(e.data.actionAdapter, e.data.guides);
      }
      if (type === 'actionAdapterUpdated') {
        dispatch(toastSuccess(e.data.message));
      }
      if (type === 'actionAdapterFailedToUpdate') {
        dispatch(toastError(e.data.message));
      }
      if (type === 'openActionAdapterRuns' && id) {
        window.open(`/admin/app-actions/${id}/runs`, '_blank', 'noopener,noreferrer');
      }
      if (type === 'showDuplicatedDialog') {
        handleShowDuplicatedDialog(e.data.actionAdapter);
      }
      if (type === 'actionAdapterDuplicated') {
        dispatch(toastSuccess(e.data.message));
      }
      if (type === 'returnToAppAction' && iframeRef.current) {
        iframeRef.current.contentWindow?.location.reload();
      }
    }
  );

  const handleAddActionAdapter = useCallback(
    (actionAdapter: Partial<ActionAdapter>, guides: GuideInfo[]) => {
      modalRef.current?.show({
        params: { actionAdapter, guides },
        subscribe: (value: unknown) => {
          if (!iframeRef.current) {
            return;
          }

          postMessageToIframe(iframeRef.current, 'updateActionAdapter', value);
        },
      });
    },
    [iframeRef]
  );

  const handleShowGuidesDialog = useCallback((actionAdapter: Partial<ActionAdapter>, guides: GuideInfo[]) => {
    guidesModalRef.current?.show({
      params: { actionAdapter, guides },
      subscribe: () => void 0,
    });
  }, []);

  const handleShowDuplicatedDialog = useCallback(
    (actionAdapter: Partial<ActionAdapter>) => {
      if (!actionAdapter.name) {
        return;
      }

      duplicateModalRef.current?.show({
        params: { actionAdapter: actionAdapter as Partial<ActionAdapter> & Pick<ActionAdapter, 'name'> },
        subscribe: (value: unknown) => {
          if (!iframeRef.current) {
            return;
          }
          postMessageToIframe(iframeRef.current, 'duplicateActionAdapter', value);
        },
      });
    },
    [iframeRef]
  );

  return (
    <>
      <FullPageModalRoute
        componentRenderer={({ root }) => <ActionAdapterNewEditor root={root} />}
        path={`/new`}
        root={root}
      />
      <FullPageModalRoute
        componentRenderer={({ id, root }) => <ActionAdapterEditor id={id} root={root} />}
        path={`/:id`}
        root={root}
      />

      <FullPageModalRoute
        componentRenderer={({ id, root }) => <ActionAdapterRunsLog id={id} root={root} />}
        path={`/:id/runs`}
        root={root}
      />

      {loading && <StyledSpinner />}
      {srcUrl && (
        <>
          <Iframe
            ref={iframeRef}
            src={`${srcUrl}?inIframe=true`}
            loading={loading}
            handleIframeLoad={handleIframeLoad}
            handleIframeError={handleIframeError}
            pageName="list"
          />
          <ActivateInactivateModal ref={modalRef} />
          <GuidesModal ref={guidesModalRef} />
          <DuplicateConfirmationModal ref={duplicateModalRef} />
        </>
      )}
    </>
  );
};

const StyledSpinner = styled(Spinner)`
  display: block;
`;

export default ActionAdapters;
